import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { useLang } from '../lib/lang';
import { getEntryTickets } from '../lib/api';
import { Ticket, Trophy, Loader2, Sparkles } from 'lucide-react';

/** Popup showing the user's individual ticket codes for a specific entry. */
export default function TicketsPopupDialog({ open, entryId, onClose }) {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !entryId) return;
    setLoading(true);
    setData(null);
    getEntryTickets(entryId)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, entryId]);

  if (!open) return null;

  const winningCode = data?.winning_code;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border-border max-w-lg max-h-[90vh] overflow-y-auto" style={{ color: '#0B3D2E' }}>
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2 !text-rolex-green">
            <Ticket className="w-5 h-5 text-gold" /> {t('myTicketsTitle') || 'Your Tickets'}
          </DialogTitle>
          {data && (
            <DialogDescription className="!text-rolex-green/70 text-sm pt-1">
              <strong>{data.raffle_title}</strong>
            </DialogDescription>
          )}
        </DialogHeader>

        {loading && (
          <div className="py-10 text-center text-rolex-green/60 text-sm flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading tickets...
          </div>
        )}

        {data && !loading && (
          <div className="space-y-4 py-3">
            {data.raffle_image && (
              <div className="flex items-center gap-3 p-3 rounded-xl bg-[hsl(43_30%_94%)] border border-[hsl(var(--gold)/0.2)]">
                <img src={data.raffle_image} alt="" className="w-16 h-16 object-cover rounded-lg shrink-0" />
                <div>
                  <p className="font-display font-bold text-sm !text-rolex-green">{data.raffle_title}</p>
                  <p className="text-xs !text-rolex-green/65">
                    {data.quantity} {data.quantity > 1 ? (t('ticketsPlural') || 'tickets') : (t('entry') || 'ticket')} ·
                    <span className="font-mono"> {data.points_spent} pts</span>
                  </p>
                </div>
              </div>
            )}

            {/* WINNING TICKET HIGHLIGHT */}
            {winningCode && (
              <motion.div
                initial={{ opacity: 0, y: 8, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                className="relative overflow-hidden rounded-2xl p-4 bg-gold-gradient shadow-[0_8px_28px_rgba(201,169,97,0.45)]"
              >
                <div className="absolute inset-0 noise-overlay opacity-25 pointer-events-none" />
                <div className="absolute -top-3 -right-3 w-16 h-16 rounded-full bg-white/30 blur-xl pointer-events-none" />
                <div className="relative flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-rolex-green flex items-center justify-center shrink-0 shadow-lg">
                    <Trophy className="w-6 h-6 text-gold" />
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] uppercase tracking-[0.22em] font-extrabold text-rolex-green leading-tight">
                      🎉 {t('winningTicket') || 'Winning ticket'}
                    </p>
                    <p className="font-mono text-2xl font-extrabold text-rolex-green tabular-nums leading-tight mt-0.5">
                      #{winningCode}
                    </p>
                    <p className="text-xs text-rolex-green/80 mt-0.5 font-medium">{t('youAreTheWinner') || 'You are the winner!'}</p>
                  </div>
                </div>
              </motion.div>
            )}

            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-rolex-green/55 font-bold mb-2.5 flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5 text-gold" />
                {t('yourTicketNumbers') || 'Your ticket numbers'} ({data.ticket_codes?.length || 0})
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {(data.ticket_codes || []).map((code, idx) => {
                  const isWinning = code === winningCode;
                  return (
                    <motion.div
                      key={`${code}-${idx}`}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.03 }}
                      className={`relative overflow-hidden rounded-lg p-3 text-center shadow-md ${
                        isWinning
                          ? 'bg-gold-gradient ring-2 ring-rolex-green animate-pulse-gold'
                          : 'bg-gradient-to-br from-[#0E4936] to-[#08291F] border-2 border-[hsl(var(--gold)/0.45)]'
                      }`}
                    >
                      <div className="absolute inset-0 noise-overlay opacity-25 pointer-events-none" />
                      <p className={`text-[8px] uppercase tracking-[0.22em] font-bold leading-tight relative ${isWinning ? 'text-rolex-green' : 'text-gold'}`}>
                        {isWinning ? '🏆 GAGNANT' : (t('ticketLabel') || 'Ticket')}
                      </p>
                      <p className={`font-mono text-xl font-extrabold leading-none mt-1 tabular-nums relative ${isWinning ? 'text-rolex-green' : 'text-gold-gradient'}`}>
                        #{code}
                      </p>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            <div className="text-[11px] text-rolex-green/55 italic leading-relaxed mt-3 px-2">
              💡 {t('ticketsTip') || 'Each ticket has a unique random code. The winning ticket will be drawn from all participating tickets at the scheduled date.'}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
